import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { SelfServiceRoute } from '@/modules/billing/components/SelfServiceRoute';
import { CandidateDetailPage } from '@/modules/candidate-detail/components/CandidateDetailPage';
import { NotFound } from '@/modules/errors/components/NotFound';
import { CoordinationRoute } from '@/modules/hiring-manager/components/CoordinationRoute';
import { PortalLayout } from '@/modules/layouts/components/PortalLayout';
import Loader from '@/modules/ui/components/loader/Loader';
import { HMChat } from '@/pages/hm/chat/HMChat';
import { HMInterviews } from '@/pages/hm/interviews/HMInterviews';
import { HMJobAutomation } from '@/pages/hm/job-automation/HMJobAutomation';
import { HMJobCandidates } from '@/pages/hm/job-candidates/HMJobCandidates';
import { HMJobEdit } from '@/pages/hm/jobs/HMJobEdit';
import { HMJobs } from '@/pages/hm/jobs/HMJobs';
import { Outreach } from '@/pages/hm/messages/Outreach';
import { HMSlackConnectResult } from '@/pages/hm/slack-connect/HMSlackConnectResult';

import { HMAvailability } from './availability/HMAvailability';
import { HMIntegrations } from './integrations/HMIntegrations';
import { HMWorkspace } from './workspace/HMWorkspace';

// Keep the Stigg SDK (including MUI and Stripe) out of the main bundle.
// PortalBillingProvider and UpgradePlanCta use the same lazy boundary so
// loading billing never blocks the portal's review pages.
const PortalBilling = lazy(() =>
  import('./billing/PortalBilling').then((module) => ({
    default: module.PortalBilling,
  })),
);

/**
 * The old create page's address, which links already sent still use (Slack welcome messages, bookmarks). Jobs are
 * created in chat, so it opens a new chat in the same workspace.
 */
function NewChatRedirect() {
  const { search } = useLocation();
  return <Navigate replace to={`/hm/chat${search}`} />;
}

export function HMApp() {
  return (
    <Routes>
      <Route path="jobs/add" element={<NewChatRedirect />} />
      <Route element={<PortalLayout />}>
        <Route index element={<HMJobs />} />
        <Route path="chat/:conversationId?" element={<HMChat />} />
        <Route element={<SelfServiceRoute />}>
          <Route path="jobs/:jobId/edit" element={<HMJobEdit />} />
          <Route path="jobs/:jobId/automation" element={<HMJobAutomation />} />
          <Route path="messages" element={<Outreach />} />
        </Route>
        <Route path="jobs/:jobId/candidates" element={<HMJobCandidates />} />
        <Route
          path="jobs/:jobId/candidates/:candidateId"
          element={<CandidateDetailPage />}
        />
        <Route
          path="slack-connect/:status"
          element={<HMSlackConnectResult />}
        />
        <Route path="integrations" element={<HMIntegrations />} />
        <Route path="workspace" element={<HMWorkspace />} />
        <Route
          path="billing"
          element={
            <Suspense fallback={<Loader />}>
              <PortalBilling />
            </Suspense>
          }
        />
        <Route element={<CoordinationRoute />}>
          <Route path="availability" element={<HMAvailability />} />
          <Route path="interviews" element={<HMInterviews />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
